import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { useEffect, useState } from 'react';
import cn from 'classnames';
import { subDays } from 'date-fns';
import { Button, Callout, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { AgreementHeader } from '~/client-flow/clientOb/WelcomeStep/AgreementHeader';
import { StepPage } from '~/client-flow/components/StepPage';
import { useResponsive } from '~/client-flow/contexts';
import RecipientDetails from '~/components/RecipientDetails/RecipientDetails';
import { useShallowStore, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { relationshipModel } from '~/models';
import { RelationshipStatus } from '~/models/relationship';
import { fromDateStampObject } from '~/models/time';
import { selectors } from '~/store/state-selectors';
import { formatShortMonthUSDate } from '~/utils';
import { replacePlaceholders } from '~/utils/introductory-message.utils';
import { TextBox } from '../../components/TextBox';
import { COBStep, getNextStepButtonText, getStepName, stepsTextsPath } from '../ClientOnboarding.utils';
import { nextStepButtonTextsPath } from '../ClientOnboardingInnerV2/ClientOnboardingV2.utils';
import { useCobConfig } from '../CobConfigContext';
import { useCobStorage } from '../CobStorageContext';
import styles from './WelcomeStep.module.scss';
import { getLimitedTimeDiscountOfferCalloutText, NEW_BUSINESS_NAME_FLAG, useWelcomeStepUtils } from './welcomeStepUtils';
export const { WelcomeStepTestIds } = getTestIds();
const ExpirationDateCallout = (param)=>{
    let { intent, children } = param;
    return /*#__PURE__*/ _jsx(Callout, {
        intent: intent,
        className: styles.expirationDateCallout,
        testId: "expiration-date-message",
        children: children
    });
};
export default function WelcomeStep(param) {
    let { onDone } = param;
    var _ref, _relationship_buyer_firstName;
    var _relationship_signatories;
    const { isMobile } = useResponsive();
    const { t: welcomeTexts } = useLocaleKeys('clientFlow.welcomeStep');
    const { t: stepsTexts } = useLocaleKeys(stepsTextsPath);
    const { t: clientFlowTexts } = useLocaleKeys(nextStepButtonTextsPath);
    const { relationship, isPreview } = useCobConfig();
    const { getFromStorage } = useCobStorage(relationship);
    const { createOrSelectBusiness, newBusinessWorkspace } = useWelcomeStepUtils({
        relationship
    });
    const [worksAt, workspaces] = useShallowStore((state)=>[
            selectors.user.business(state),
            selectors.user.workspaces(state)
        ]);
    const { logBI } = useStoreActions();
    const hasAdditionalRecipients = relationshipModel.hasAdditionalRecipients(relationship);
    const getSelectedWorkspace = ()=>{
        const shouldUseRelationshipCustomer = relationship.isSample || isPreview;
        const isNewBusinessSelected = relationship.suggestedCustomerName && !(newBusinessWorkspace === null || newBusinessWorkspace === void 0 ? void 0 : newBusinessWorkspace.id()) && getFromStorage('select_new_business') !== false;
        const newBusiness = {
            id: NEW_BUSINESS_NAME_FLAG,
            name: relationship.suggestedCustomerName
        };
        return shouldUseRelationshipCustomer ? relationship.customer : isNewBusinessSelected ? newBusiness : worksAt || (workspaces === null || workspaces === void 0 ? void 0 : workspaces[0].toIdAndName());
    };
    const getLimitedTimeDiscountOfferCallout = ()=>{
        const calloutText = getLimitedTimeDiscountOfferCalloutText(relationship);
        if (!calloutText) return null;
        return /*#__PURE__*/ _jsx(Callout, {
            intent: "info",
            testId: "limited-time-discount-offer-callout",
            withBorder: true,
            children: calloutText
        });
    };
    const [selectedWorkspace, setSelectedWorkspace] = useState(getSelectedWorkspace());
    useEffect(()=>{
        setSelectedWorkspace(getSelectedWorkspace());
    }, [
        worksAt,
        relationship
    ]);
    return /*#__PURE__*/ _jsx(StepPage, {
        showDownloadAsPDFButton: true,
        disableDownloadAsPDFButton: isPreview,
        header: getStepName(COBStep.welcome, relationship, stepsTexts),
        testId: WelcomeStepTestIds.Root(),
        primaryBtn: /*#__PURE__*/ _jsx(Button, {
            bi: {
                name: 'welcome_step_continue'
            },
            onClick: ()=>{
                if (!isPreview) {
                    logBI(10801);
                }
                if ((selectedWorkspace === null || selectedWorkspace === void 0 ? void 0 : selectedWorkspace.id) === NEW_BUSINESS_NAME_FLAG) {
                    createOrSelectBusiness(NEW_BUSINESS_NAME_FLAG);
                }
                onDone();
            },
            children: getNextStepButtonText(COBStep.welcome, relationship, clientFlowTexts)
        }),
        children: /*#__PURE__*/ _jsxs(TextBox, {
            children: [
                relationship.expirationDate && (relationship.status === RelationshipStatus.Expired ? /*#__PURE__*/ _jsx(ExpirationDateCallout, {
                    intent: "warning",
                    children: "This proposal expired on ".concat(formatShortMonthUSDate(fromDateStampObject(relationship.expirationDate)), ".")
                }) : /*#__PURE__*/ _jsx(ExpirationDateCallout, {
                    intent: "info",
                    children: "Please note that this proposal is valid until ".concat(formatShortMonthUSDate(subDays(fromDateStampObject(relationship.expirationDate), 1)), ".")
                })),
                getLimitedTimeDiscountOfferCallout(),
                /*#__PURE__*/ _jsx(AgreementHeader, {
                    relationship: relationship,
                    companyName: (_ref = selectedWorkspace === null || selectedWorkspace === void 0 ? void 0 : selectedWorkspace.name) !== null && _ref !== void 0 ? _ref : '',
                    className: cn(styles.withHorizontalBorder, {
                        [styles.mobile]: isMobile
                    }),
                    isPreview: isPreview
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph1",
                    showWhiteSpaces: true,
                    breakWords: true,
                    className: cn(styles.coverLetter, {
                        [styles.mobile]: isMobile
                    }),
                    children: replacePlaceholders(relationship.customNotificationText, (_relationship_buyer_firstName = relationship.buyer.firstName) !== null && _relationship_buyer_firstName !== void 0 ? _relationship_buyer_firstName : '', relationship.vendor.name)
                }),
                hasAdditionalRecipients && /*#__PURE__*/ _jsxs("div", {
                    "data-testid": "additional-recipients-section",
                    className: styles.withHorizontalBorder,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            weight: "normal",
                            children: welcomeTexts.additionalRecipients()
                        }),
                        (_relationship_signatories = relationship.signatories) === null || _relationship_signatories === void 0 ? void 0 : _relationship_signatories.map((signatory, index)=>/*#__PURE__*/ _createElement(RecipientDetails, _object_spread_props(_object_spread({}, signatory), {
                                key: signatory.id,
                                badge: relationshipModel.hasSignatorySigned(relationship, signatory.id) ? 'signed' : 'required',
                                testId: "welcome-step-signatory-".concat(index)
                            }))),
                        relationship.reviewers.map((reviewer, index)=>/*#__PURE__*/ _createElement(RecipientDetails, _object_spread_props(_object_spread({}, reviewer), {
                                key: reviewer.id,
                                testId: "welcome-step-reviewer-".concat(index)
                            })))
                    ]
                })
            ]
        })
    });
}
